import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import * as Sentry from '@sentry/react';
import './i18n'; // Initialize i18next
import { SnackbarProvider } from './components/SnackbarProvider';
import { ColorModeContextProvider } from './contexts/ColorModeContext';
import { LanguageContextProvider } from './contexts/LanguageContext';
import { CurrencyContextProvider } from './contexts/CurrencyContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { AnimationsProvider } from './contexts/AnimationsContext';
import { MenuLayoutProvider } from './contexts/MenuLayoutContext';
import { ZoomProvider } from './contexts/ZoomContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { MobileNavigationProvider } from './contexts/MobileNavigationContext';
import { OfflineContextProvider } from './contexts/OfflineContext';
import { SyncContextProvider } from './contexts/SyncContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { queryClient } from './lib/queryClient';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/login';
import { LandingPage } from './pages/landing';
import { ForgotPasswordPage } from './pages/forgot-password';
import { ResetPasswordPage } from './pages/reset-password';

// Import pages (will be created later)
import { DashboardPage } from './pages/dashboard';
import { ProductList } from './pages/products/list';
import { ProductCreate } from './pages/products/create';
import { ProductEdit } from './pages/products/edit';
import { ProductShow } from './pages/products/show';
import { InventoryPage, InventoryCreate } from './pages/inventory';
import { OrdersList, OrderShow } from './pages/orders';
import { PendingPayments } from './pages/orders/pending-payments';
import { POSPage } from './pages/pos';
import { StaffList } from './pages/staff/list';
import { StaffCreate } from './pages/staff/create';
import { StaffEdit } from './pages/staff/edit';
import { CustomersPage } from './pages/customers';
import { CustomerShow } from './pages/customers/show';
import { CustomerHistoryPage } from './pages/customers/history';
import { LocationList, LocationCreate, LocationEdit, LocationShow } from './pages/locations';
import { StockTransferList, StockTransferCreate, StockTransferShow } from './pages/stock-transfers';
import { DeliveriesPage } from './pages/deliveries/DeliveriesPage';
import { DeliveryShow } from './pages/deliveries/show';
import { RouteShow } from './pages/deliveries/route-show';
import { Settings } from './pages/settings';

// B2B Module Pages
import { QuoteList, QuoteCreate, QuoteEdit, QuoteShow } from './pages/quotes';
import { InvoiceList, InvoiceCreate, InvoiceEdit, InvoiceShow } from './pages/invoices';
import { PricingTierList, PricingTierCreate, PricingTierEdit } from './pages/pricing-tiers';

// B2B Portal Pages
import { AccessRequestList } from './pages/access-requests/list';
import { ResellersPage } from './pages/resellers';
import { ResellerShow } from './pages/resellers/show';

// Roles & Permissions Pages
import { RoleList, RoleCreate, RoleEdit } from './pages/roles';

// Suppliers Pages
import { SuppliersPage, SupplierShow } from './pages/suppliers';

// Cash Drawer Pages
import { CashDrawerPage, CashDrawerShow } from './pages/cash-drawer';
import { CreditPage, CreditShow } from './pages/credit';

// Restocks Pages
import { RestocksList, RestockCreate, RestockShow } from './pages/restocks';

// Expenses Pages
import { ExpensesPage } from './pages/expenses';

// Section Pages (for Tabs mode)
import { SectionPage } from './pages/sections';

// B2B Sales Pages
import { B2BSalesPage } from './pages/sales-b2b';

// Upgrade Page
import { UpgradePage } from './pages/UpgradePage';

// Analytics Pages
import { SalesForecast, DemandPlanning, ProfitAnalysis } from './pages/analytics';

// Redirect wrapper for legacy routes with path params
const RedirectToOrderShow = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/orders/show/${id}`} replace />;
};

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong</p>}>
      <ErrorBoundary>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <QueryClientProvider client={queryClient}>
          <OfflineContextProvider>
              <ZoomProvider>
              <MenuLayoutProvider>
                <LanguageContextProvider>
                  <CurrencyContextProvider>
                    <ColorModeContextProvider>
                      <AnimationsProvider>
                      <NotificationsProvider>
                      <CssBaseline />
                      <GlobalStyles styles={{ html: { WebkitFontSmoothing: 'auto' } }} />
                      <SnackbarProvider>
                        <SyncContextProvider>
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

              {/* Protected routes - App Dashboard */}
              <Route
                path="/app"
                element={
                  <ProtectedRoute>
                    <PermissionsProvider>
                      <MobileNavigationProvider>
                        <Layout />
                      </MobileNavigationProvider>
                    </PermissionsProvider>
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />

                <Route path="products">
                  <Route index element={<ProductList />} />
                  <Route path="create" element={<ProductCreate />} />
                  <Route path="edit/:id" element={<ProductEdit />} />
                  <Route path="show/:id" element={<ProductShow />} />
                </Route>

                <Route path="inventory">
                  <Route index element={<InventoryPage />} />
                  <Route path="create" element={<InventoryCreate />} />
                  {/* Redirect old routes to tabbed page */}
                  <Route path="movements" element={<Navigate to="/app/inventory?tab=movements" replace />} />
                  <Route path="forecast" element={<Navigate to="/app/inventory?tab=forecast" replace />} />
                </Route>

                {/* Unified Orders Routes */}
                <Route path="orders">
                  <Route index element={<OrdersList />} />
                  <Route path="show/:id" element={<OrderShow />} />
                </Route>

                {/* Pending Payments Dashboard */}
                <Route path="pending-payments" element={<PendingPayments />} />

                {/* Legacy reservations routes - redirect to unified orders */}
                <Route path="reservations">
                  <Route index element={<Navigate to="/app/orders?orderType=B2C" replace />} />
                  <Route path="show/:id" element={<RedirectToOrderShow />} />
                </Route>

                {/* Legacy sales routes - redirect to unified orders */}
                <Route path="sales">
                  <Route index element={<Navigate to="/app/orders" replace />} />
                  <Route path="create" element={<Navigate to="/app/pos" replace />} />
                  <Route path="show/:id" element={<RedirectToOrderShow />} />
                </Route>

                {/* B2B Sales Routes */}
                <Route path="sales-b2b" element={<B2BSalesPage />} />

                <Route path="pos" element={<POSPage />} />

                <Route path="staff">
                  <Route index element={<StaffList />} />
                  <Route path="create" element={<StaffCreate />} />
                  <Route path="edit/:id" element={<StaffEdit />} />
                </Route>

                <Route path="customers">
                  <Route index element={<CustomersPage />} />
                  <Route path=":id" element={<CustomerShow />} />
                  <Route path=":id/history" element={<CustomerHistoryPage />} />
                </Route>

                <Route path="locations">
                  <Route index element={<LocationList />} />
                  <Route path="create" element={<LocationCreate />} />
                  <Route path=":id/edit" element={<LocationEdit />} />
                  <Route path=":id" element={<LocationShow />} />
                </Route>

                <Route path="stock-transfers">
                  <Route index element={<StockTransferList />} />
                  <Route path="create" element={<StockTransferCreate />} />
                  <Route path=":id" element={<StockTransferShow />} />
                </Route>

                <Route path="deliveries">
                  <Route index element={<DeliveriesPage />} />
                  <Route path=":id" element={<DeliveryShow />} />
                  <Route path="routes/:id" element={<RouteShow />} />
                  {/* Redirect old schedule route to tabbed page */}
                  <Route path="schedule" element={<Navigate to="/app/deliveries?tab=schedule" replace />} />
                </Route>

                {/* B2B Module Routes */}
                <Route path="quotes">
                  <Route index element={<QuoteList />} />
                  <Route path="create" element={<QuoteCreate />} />
                  <Route path="edit/:id" element={<QuoteEdit />} />
                  <Route path="show/:id" element={<QuoteShow />} />
                </Route>

                <Route path="invoices">
                  <Route index element={<InvoiceList />} />
                  <Route path="create" element={<InvoiceCreate />} />
                  <Route path="edit/:id" element={<InvoiceEdit />} />
                  <Route path="show/:id" element={<InvoiceShow />} />
                </Route>

                <Route path="pricing-tiers">
                  <Route index element={<PricingTierList />} />
                  <Route path="create" element={<PricingTierCreate />} />
                  <Route path="edit/:id" element={<PricingTierEdit />} />
                </Route>

                {/* B2B Portal Routes */}
                <Route path="access-requests" element={<AccessRequestList />} />
                <Route path="resellers">
                  <Route index element={<ResellersPage />} />
                  <Route path=":id" element={<ResellerShow />} />
                </Route>

                {/* Roles & Permissions Routes */}
                <Route path="roles">
                  <Route index element={<RoleList />} />
                  <Route path="create" element={<RoleCreate />} />
                  <Route path="edit/:id" element={<RoleEdit />} />
                </Route>

                {/* Suppliers Routes */}
                <Route path="suppliers">
                  <Route index element={<SuppliersPage />} />
                  <Route path=":id" element={<SupplierShow />} />
                </Route>

                {/* Cash Drawer Routes */}
                <Route path="cash-drawer">
                  <Route index element={<CashDrawerPage />} />
                  <Route path=":id" element={<CashDrawerShow />} />
                </Route>

                {/* Customer Credit Routes */}
                <Route path="credit">
                  <Route index element={<CreditPage />} />
                  <Route path=":id" element={<CreditShow />} />
                </Route>

                {/* Restocks Routes */}
                <Route path="restocks">
                  <Route index element={<RestocksList />} />
                  <Route path="create" element={<RestockCreate />} />
                  <Route path="edit/:id" element={<RestockCreate />} />
                  <Route path=":id" element={<RestockShow />} />
                </Route>

                {/* Expenses Routes */}
                <Route path="expenses" element={<ExpensesPage />} />

                {/* Analytics Routes */}
                <Route path="analytics">
                  <Route path="forecast" element={<SalesForecast />} />
                  <Route path="demand" element={<DemandPlanning />} />
                  <Route path="profit" element={<ProfitAnalysis />} />
                </Route>

                {/* Upgrade Page */}
                <Route path="upgrade" element={<UpgradePage />} />

                <Route path="settings" element={<Settings />} />

                {/* Section Routes (for Tabs mode navigation) */}
                <Route path="section/:sectionId" element={<SectionPage />} />

                <Route path="*" element={<Navigate to="/app" replace />} />
              </Route>
                    </Routes>
                        </SyncContextProvider>
                      </SnackbarProvider>
                      </NotificationsProvider>
                    </AnimationsProvider>
                    </ColorModeContextProvider>
                  </CurrencyContextProvider>
                </LanguageContextProvider>
              </MenuLayoutProvider>
              </ZoomProvider>
          </OfflineContextProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
}

export default App;
